import axios from 'axios';
import { 
  isOnline, 
  queueOfflineChange, 
  getSavedUserSession,
  cacheMedicalData,
  getCachedMedicalData 
} from '@/services/offlineStorage';

/**
 * API Configuration for Standalone Mobile App
 * 
 * Architecture:
 * - App UI is bundled locally (no remote website)
 * - API calls go to your hosted backend (Railway, Render, etc.)
 * - Offline mode uses locally cached data
 * - Changes queue and sync when back online
 */

// Production backend URL - set in .env.production before building
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL 
  ? (process.env.REACT_APP_BACKEND_URL.endsWith('/api') 
      ? process.env.REACT_APP_BACKEND_URL 
      : `${process.env.REACT_APP_BACKEND_URL}/api`)
  : null;

// Warn if no backend URL configured (only in dev)
if (!BACKEND_URL && process.env.NODE_ENV === 'development') {
  console.warn('[API] No REACT_APP_BACKEND_URL configured. Set this in .env.production before building for app stores.');
}

const API = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Check if we're currently online
 */
export const checkOnline = isOnline;

/**
 * Get session token from storage or cookie
 */
export const getSessionToken = async () => {
  // Try to get from saved session first (for native app)
  const { token } = await getSavedUserSession();
  if (token) return token;
  
  // Fallback to cookie (for web)
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'session_token') {
      return value;
    }
  }
  return null;
};

/**
 * Clear session data on logout
 */
export const clearSession = () => {
  document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  localStorage.removeItem('user_preferences');
};

// Request interceptor - add auth token
API.interceptors.request.use(
  async (config) => {
    const token = await getSessionToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors and offline
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Network error - we're offline
    if (!error.response && error.message === 'Network Error') {
      console.log('[API] Offline - using cached data');
      
      // For GET requests, try to return cached data
      if (originalRequest.method === 'get') {
        const cachedData = await getCachedDataForRequest(originalRequest.url);
        if (cachedData) {
          return { data: cachedData, _fromCache: true };
        }
      }
      
      // For write operations, queue for later sync
      // EXCEPT account deletion - this must never be queued (could falsely report success)
      if (['post', 'put', 'patch', 'delete'].includes(originalRequest.method)) {
        // Never queue account deletion - it must complete synchronously
        if (originalRequest.url.includes('/auth/account') && originalRequest.method === 'delete') {
          error.isOffline = true;
          error.message = 'Cannot delete account while offline. Please connect to the internet and try again.';
          return Promise.reject(error);
        }
        
        await queueOfflineChange({
          method: originalRequest.method,
          url: originalRequest.url,
          data: originalRequest.data,
        });
        
        return { 
          data: { queued: true, message: 'Change saved offline. Will sync when online.' },
          _queued: true 
        };
      }
      
      error.isOffline = true;
    }
    
    // Auth error
    if (error.response?.status === 401) {
      clearSession();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * Get cached data for a specific API request
 */
async function getCachedDataForRequest(url) {
  // Map URL patterns to cache keys
  const cacheMap = {
    '/shared-medical/medications': 'medications',
    '/shared-medical/allergies': 'allergies',
    '/shared-medical/conditions': 'conditions',
    '/shared-medical/doctors': 'doctors',
    '/shared-medical/surgeries': 'surgeries',
    '/shared-medical/vaccinations': 'vaccinations',
    '/shared-medical/profile': 'medical_profile',
    '/shared-medical/emergency-contacts': 'emergency_contacts',
    '/shared-medical/insurance': 'insurance',
    '/appointments': 'appointments',
  };
  
  for (const [pattern, cacheKey] of Object.entries(cacheMap)) {
    if (url.includes(pattern)) {
      return await getCachedMedicalData(cacheKey);
    }
  }
  
  return null;
}

/**
 * Wrapper for API calls with automatic caching
 */
export const apiWithCache = {
  async get(url, config) {
    try {
      const response = await API.get(url, config);
      
      // Cache the response for offline use
      if (!response._fromCache) {
        const cacheKey = getCacheKeyForUrl(url);
        if (cacheKey) {
          await cacheMedicalData(cacheKey, response.data);
        }
      }
      
      return response;
    } catch (error) {
      // If offline, try cache
      if (error.isOffline) {
        const cachedData = await getCachedDataForRequest(url);
        if (cachedData) {
          return { data: cachedData, _fromCache: true };
        }
      }
      throw error;
    }
  },
  
  post: API.post.bind(API),
  put: API.put.bind(API),
  patch: API.patch.bind(API),
  delete: API.delete.bind(API),
};

/**
 * Get cache key for a URL
 */
function getCacheKeyForUrl(url) {
  const keyMap = {
    'medications': 'MEDICATIONS',
    'allergies': 'ALLERGIES',
    'conditions': 'CONDITIONS',
    'doctors': 'DOCTORS',
    'surgeries': 'SURGERIES',
    'vaccinations': 'VACCINATIONS',
    'profile': 'MEDICAL_PROFILE',
    'emergency-contacts': 'EMERGENCY_CONTACTS',
    'insurance': 'INSURANCE',
    'appointments': 'APPOINTMENTS',
  };
  
  for (const [pattern, key] of Object.entries(keyMap)) {
    if (url.includes(pattern)) {
      return key;
    }
  }
  return null;
}

export default API;
